/** Fetch solutions from the server-protected /sols tree. */
import { getWorldId, loadWorldsManifest } from './worldLoader';
import { LecturerCredentials, LecturerAuthenticationError, lecturerAuthorization } from './lecturerAuth';

export interface LevelSolution {
  levelId: string;
  solution: string;
  alternativeSolutions?: string[];
}

export interface WorldSolutions {
  worldId: string;
  solutions: LevelSolution[];
}

export async function fetchWorldSolutions(
  worldId: string,
  credentials?: LecturerCredentials,
  signal?: AbortSignal,
): Promise<WorldSolutions | null> {
  if (!credentials) throw new LecturerAuthenticationError();
  const manifest = await loadWorldsManifest();
  const entry = manifest.worlds.find(world => getWorldId(world) === worldId);
  const solutionsFile = entry?.solutionsFile ?? `${worldId}-solutions.json`;
  const response = await fetch(`/sols/${solutionsFile}`, {
    method: 'GET',
    headers: { Accept: 'application/json', Authorization: lecturerAuthorization(credentials) },
    credentials: 'omit', // Use the active app login, not browser-cached Basic Auth.
    cache: 'no-store',
    signal,
  });
  if (response.status === 401 || response.status === 403) throw new LecturerAuthenticationError();
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to fetch solutions: ${response.status}`);

  const data = await response.json();
  if (!data || data.worldId !== worldId || !Array.isArray(data.solutions) ||
      !data.solutions.every((item: LevelSolution) => item && typeof item.levelId === 'string' &&
        typeof item.solution === 'string' && (item.alternativeSolutions === undefined ||
          (Array.isArray(item.alternativeSolutions) && item.alternativeSolutions.every(code => typeof code === 'string'))))) {
    throw new Error('Invalid solutions file.');
  }
  return data as WorldSolutions;
}

async function findLevelSolution(
  worldId: string,
  levelId: string,
  cache?: Map<string, WorldSolutions>,
  credentials?: LecturerCredentials,
): Promise<LevelSolution | null> {
  if (!credentials) throw new LecturerAuthenticationError();
  let world = cache?.get(worldId);
  if (!world) {
    const fetched = await fetchWorldSolutions(worldId, credentials);
    if (!fetched) return null;
    world = fetched;
    cache?.set(worldId, world);
  }
  return world.solutions.find(item => item.levelId === levelId) ?? null;
}

// Authentication and network errors must propagate so callers can retry or log in.
export async function getLevelSolution(
  worldId: string,
  levelId: string,
  solutionsCache?: Map<string, WorldSolutions>,
  credentials?: LecturerCredentials,
): Promise<string | null> {
  const level = await findLevelSolution(worldId, levelId, solutionsCache, credentials);
  return level?.solution || null;
}

export async function getLevelAlternativeSolutions(
  worldId: string,
  levelId: string,
  solutionsCache?: Map<string, WorldSolutions>,
  credentials?: LecturerCredentials,
): Promise<string[] | null> {
  const level = await findLevelSolution(worldId, levelId, solutionsCache, credentials);
  return level?.alternativeSolutions ?? null;
}
